import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import MarketingNav from '../components/MarketingNav';
import { ArrowRight, Upload, Palette, Share2, CreditCard, Globe, Wand2, Package, BarChart3, ShieldCheck, MousePointerClick, CheckCircle2, ArrowDown } from 'lucide-react';
import { useRef, useEffect } from 'react';

function useScrollAnimate() {
  const ref = useRef(null);
  useEffect(() => { const el = ref.current; if (!el) return; const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.style.opacity = '1'; el.style.transform = 'translateY(0)'; } }, { threshold: 0.1 }); obs.observe(el); return () => obs.disconnect(); }, []);
  return ref;
}
function Reveal({ children, className = '', delay = 0 }) {
  const ref = useScrollAnimate();
  return <div ref={ref} className={className} style={{ opacity: 0, transform: 'translateY(32px)', transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s` }}>{children}</div>;
}

// Step component with number, visual, and detailed content
function Step({ num, title, desc, details, mockup, dark }) {
  const bg = dark ? '#195259' : '#FFFFFF';
  const textMain = dark ? '#FFFFFF' : '#195259';
  const textSub = dark ? 'rgba(245,233,207,0.7)' : '#6D6E71';
  const reversed = num % 2 === 0;

  return (
    <section className="py-16 sm:py-24 px-4" style={{ backgroundColor: bg }}>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal className={reversed ? 'order-1 lg:order-2' : ''}>
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="h-14 w-14 rounded-2xl flex items-center justify-center text-xl font-extrabold shrink-0" style={{ backgroundColor: '#FF9A00', color: '#195259' }}>
                  {String(num).padStart(2, '0')}
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[3px]" style={{ color: '#FF9A00' }}>Step {num}</p>
                  <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={{ color: textMain }}>{title}</h2>
                </div>
              </div>
              <p className="text-base leading-relaxed mb-6" style={{ color: textSub }}>{desc}</p>
              <div className="space-y-3">
                {details.map((d, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#FF9A00' }} />
                    <div>
                      <p className="text-sm font-semibold" style={{ color: textMain }}>{d.title}</p>
                      <p className="text-xs leading-relaxed mt-0.5" style={{ color: textSub }}>{d.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className={reversed ? 'order-2 lg:order-1' : ''}>
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl" style={{ background: `linear-gradient(135deg, ${dark ? 'rgba(255,154,0,0.15)' : 'rgba(25,82,89,0.08)'}, transparent)`, transform: reversed ? 'rotate(-2deg)' : 'rotate(2deg)' }} />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ border: dark ? '1px solid rgba(255,255,255,0.1)' : '1.5px solid #195259' }}>
                {mockup}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function HowItWorksPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ fontFamily: 'Lexend, sans-serif' }}>

      {/* Nav */}
      <MarketingNav activePage="/how-it-works" />

      {/* Hero */}
      <section className="relative pt-28 pb-16 sm:pt-36 sm:pb-20 px-4 overflow-hidden" style={{ backgroundColor: '#195259' }}>
        <img src="/images/marketing/element-teal-right.png" alt="" className="absolute top-0 right-0 w-[140px] sm:w-[200px] lg:w-[280px] h-auto pointer-events-none select-none" style={{ zIndex: 1, opacity: 0.5 }} />
        <img src="/images/marketing/element-teal-left.png" alt="" className="absolute bottom-0 left-0 w-[100px] sm:w-[160px] lg:w-[220px] h-auto pointer-events-none select-none" style={{ zIndex: 1, opacity: 0.5 }} />
        <div className="max-w-4xl mx-auto text-center relative" style={{ zIndex: 2 }}>
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-[3px] mb-4" style={{ color: '#FF9A00' }}>How It Works</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight mb-6">
              From zero to selling<br /><span style={{ color: '#FF9A00' }}>in 5 steps.</span>
            </h1>
            <p className="text-lg max-w-2xl mx-auto mb-8" style={{ color: '#F5E9CF', opacity: 0.8 }}>
              No tech skills needed. No complicated setup. Just follow these steps and you'll be making money from your digital products.
            </p>
            <ArrowDown className="h-6 w-6 mx-auto animate-bounce" style={{ color: '#FF9A00' }} />
          </Reveal>
        </div>
      </section>

      {/* ═══ STEP 1: Create Account ═══ */}
      <Step num={1} dark={false}
        title="Create your free account"
        desc="Sign up with your email or Google account. It takes less than 30 seconds. No credit card needed — you can start with the free plan and upgrade whenever you're ready."
        details={[
          { title: 'Email or Google Sign-Up', text: 'Use your email address or sign in with Google. One click and you\'re in.' },
          { title: 'Set up your store name', text: 'Choose a store name that becomes your unique link: funnely.com/yourstore' },
          { title: 'Complete your profile', text: 'Add your avatar, bio, and social links. This shows on your landing pages.' },
        ]}
        mockup={
          <div className="p-5" style={{ backgroundColor: '#F5E9CF' }}>
            <div className="rounded-xl p-5 bg-white" style={{ border: '1px solid #19525915' }}>
              <div className="text-center mb-4">
                <div className="h-12 w-12 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: '#195259' }}><Globe className="h-5 w-5 text-white" /></div>
                <p className="text-sm font-bold" style={{ color: '#195259' }}>Create your account</p>
              </div>
              <div className="space-y-2.5 mb-4">
                <div className="h-9 rounded-lg px-3 flex items-center text-[9px]" style={{ backgroundColor: '#F5E9CF', color: '#6D6E71' }}>yourname@email.com</div>
                <div className="h-9 rounded-lg px-3 flex items-center text-[9px]" style={{ backgroundColor: '#F5E9CF', color: '#6D6E71' }}>Create a password</div>
              </div>
              <div className="h-10 rounded-lg flex items-center justify-center text-[10px] font-semibold text-white" style={{ backgroundColor: '#195259' }}>Sign Up Free</div>
              <div className="flex items-center gap-2 mt-3"><div className="flex-1 h-px" style={{ backgroundColor: '#19525920' }} /><span className="text-[8px]" style={{ color: '#6D6E71' }}>or</span><div className="flex-1 h-px" style={{ backgroundColor: '#19525920' }} /></div>
              <div className="h-9 rounded-lg flex items-center justify-center gap-2 mt-2 text-[9px] font-medium" style={{ border: '1px solid #19525920', color: '#195259' }}>
                <div className="h-4 w-4 rounded-full" style={{ backgroundColor: '#4285F4' }} /> Continue with Google
              </div>
            </div>
          </div>
        }
      />

      {/* ═══ STEP 2: Add Products ═══ */}
      <Step num={2} dark={true}
        title="Upload your digital product"
        desc="Upload any digital file — ebooks, templates, presets, courses, audio, video, ZIP files. Set your price, currency, description, and download limits. You can also create free products as lead magnets."
        details={[
          { title: 'Upload any file type', text: 'PDF, ZIP, MP4, MP3, DOCX — any file works. Up to 100MB per product.' },
          { title: 'Set pricing & currency', text: 'Choose MYR, USD, or IDR. Set any price, or make it free.' },
          { title: 'Configure download limits', text: 'Set how many times a customer can download. Set expiry period (24h, 72h, 7 days, etc.).' },
          { title: 'Create bundles', text: 'Bundle multiple products together at a discounted price to increase order value.' },
        ]}
        mockup={
          <div className="p-4" style={{ backgroundColor: '#0F3338' }}>
            <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <p className="text-[9px] font-semibold text-white/60 mb-3">New Product</p>
              <div className="space-y-2.5">
                <div className="h-8 rounded-lg px-3 flex items-center text-[8px]" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#FF9A00' }}>Lightroom Preset Pack Vol.2</div>
                <div className="h-16 rounded-lg border-2 border-dashed flex flex-col items-center justify-center" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                  <Upload className="h-4 w-4 mb-1" style={{ color: 'rgba(255,255,255,0.3)' }} />
                  <p className="text-[7px]" style={{ color: 'rgba(255,255,255,0.3)' }}>presets-v2.zip (12.4 MB)</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="h-8 rounded-lg px-3 flex items-center text-[8px]" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}>RM 49.00</div>
                  <div className="h-8 rounded-lg px-3 flex items-center text-[8px]" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}>5 downloads</div>
                </div>
                <div className="h-9 rounded-lg flex items-center justify-center text-[9px] font-semibold" style={{ backgroundColor: '#FF9A00', color: '#195259' }}>Create Product</div>
              </div>
            </div>
          </div>
        }
      />

      {/* ═══ STEP 3: Build Landing Page ═══ */}
      <Step num={3} dark={false}
        title="Build your sales page"
        desc="This is where the magic happens. Pick from 5 premium templates or let AI write your entire page. Use the click-to-edit builder to customize every element. Add features, testimonials, FAQ, countdown timers, and more."
        details={[
          { title: 'Choose a template', text: 'Pick from Business Pro, Dark Luxe, Storefront Showcase, Minimal Mono, or Bold Split.' },
          { title: 'Or use AI Builder', text: 'Describe your business and AI generates a complete, high-converting sales page with copywriting.' },
          { title: 'Click-to-edit anything', text: 'Click any element in the preview to edit it directly. No forms, no sidebars — just click and type.' },
          { title: 'Add sections', text: 'Insert headlines, images, feature grids, testimonials, FAQ, countdown timers, CTA buttons — in any order.' },
          { title: 'Select your products', text: 'Choose which products to display on this page. Set product layout (list or cards) and checkout style.' },
        ]}
        mockup={
          <div className="p-4" style={{ backgroundColor: '#F5E9CF' }}>
            <div className="grid grid-cols-5 gap-2">
              <div className="col-span-3 rounded-lg p-3 bg-white" style={{ border: '1px solid #19525915' }}>
                <div className="h-3 w-20 rounded bg-[#195259]/10 mx-auto mb-2" />
                <div className="h-5 w-40 rounded bg-[#195259]/20 mx-auto mb-1.5" />
                <div className="h-2 w-32 rounded bg-[#195259]/10 mx-auto mb-3" />
                <div className="grid grid-cols-3 gap-1.5 mb-3">
                  {[1,2,3].map(i => <div key={i} className="h-12 rounded-md" style={{ backgroundColor: '#F5E9CF', border: '1px solid #19525910' }}><div className="h-3 w-3 rounded mx-auto mt-3" style={{ backgroundColor: '#FF9A00', opacity: 0.4 }} /></div>)}
                </div>
                <div className="h-6 w-24 rounded-lg mx-auto" style={{ backgroundColor: '#FF9A00', opacity: 0.7 }} />
              </div>
              <div className="col-span-2 rounded-lg p-2 bg-white" style={{ border: '1px solid #19525915' }}>
                <div className="flex gap-0.5 mb-2">{['Edit', 'Products', 'Design'].map(t => <div key={t} className="flex-1 text-center py-0.5 rounded text-[5px] font-medium" style={{ backgroundColor: t === 'Design' ? '#195259' : 'transparent', color: t === 'Design' ? 'white' : '#6D6E71' }}>{t}</div>)}</div>
                <div className="space-y-1.5">
                  {[['Primary', '#FF9A00'], ['Background', '#F5E9CF'], ['Text', '#195259']].map(([l, c]) => (
                    <div key={l} className="flex items-center gap-1.5 p-1.5 rounded" style={{ backgroundColor: '#F5E9CF' }}>
                      <div className="h-4 w-4 rounded border" style={{ backgroundColor: c }} />
                      <span className="text-[6px] font-medium" style={{ color: '#195259' }}>{l}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        }
      />

      {/* ═══ STEP 4: Connect Payment ═══ */}
      <Step num={4} dark={true}
        title="Connect your payment gateway"
        desc="Go to Settings and connect your preferred payment gateway. Funnely supports 6 payment gateways across Malaysia, Indonesia, and international markets. Once connected, customers can pay directly on your landing page."
        details={[
          { title: 'Malaysia: Billplz, ToyyibPay, SenangPay', text: 'Accept FPX, credit/debit cards, and e-wallets (Touch n Go, Boost, GrabPay).' },
          { title: 'International: Stripe', text: 'Accept Visa, Mastercard, and international cards from anywhere in the world.' },
          { title: 'Indonesia: Midtrans, Xendit', text: 'Accept GoPay, OVO, Dana, bank transfer, QRIS, and Indonesian cards.' },
          { title: 'One-time setup', text: 'Enter your API keys once. All your landing pages and checkout forms use the same gateway automatically.' },
        ]}
        mockup={
          <div className="p-4" style={{ backgroundColor: '#0F3338' }}>
            <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <p className="text-[9px] font-semibold text-white/60 mb-3">Payment Gateways</p>
              <div className="space-y-2">
                {[
                  { name: 'Billplz', status: 'Connected', active: true },
                  { name: 'Stripe', status: 'Connected', active: true },
                  { name: 'ToyyibPay', status: 'Not configured', active: false },
                  { name: 'SenangPay', status: 'Not configured', active: false },
                ].map(g => (
                  <div key={g.name} className="rounded-lg p-3 flex items-center justify-between" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: `1px solid ${g.active ? 'rgba(255,154,0,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                    <div className="flex items-center gap-2.5">
                      <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: g.active ? '#FF9A00' : 'rgba(255,255,255,0.06)' }}>
                        <CreditCard className="h-3.5 w-3.5" style={{ color: g.active ? '#195259' : 'rgba(255,255,255,0.3)' }} />
                      </div>
                      <div>
                        <p className="text-[9px] font-semibold text-white">{g.name}</p>
                        <p className="text-[7px]" style={{ color: g.active ? '#FF9A00' : 'rgba(255,255,255,0.3)' }}>{g.status}</p>
                      </div>
                    </div>
                    {g.active && <CheckCircle2 className="h-4 w-4" style={{ color: '#FF9A00' }} />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        }
      />

      {/* ═══ STEP 5: Share & Sell ═══ */}
      <Step num={5} dark={false}
        title="Share your link and start selling"
        desc="Hit Publish, copy your unique link, and paste it anywhere — Instagram bio, TikTok, WhatsApp, Twitter, email signature. Every click on that link takes people directly to your sales page with built-in checkout. That's it. You're live."
        details={[
          { title: 'One-click publish', text: 'Click Publish in your editor. Your page goes live instantly.' },
          { title: 'Copy your link', text: 'Your unique URL: funnely.com/yourstore/your-page. Share it anywhere.' },
          { title: 'Link-in-bio ready', text: 'Perfect for Instagram, TikTok, Twitter bio. One link, all your products.' },
          { title: 'Track everything', text: 'See page views, unique visitors, conversions, and revenue in real time from your dashboard.' },
        ]}
        mockup={
          <div className="p-5" style={{ backgroundColor: '#F5E9CF' }}>
            <div className="rounded-xl p-4 bg-white mb-3" style={{ border: '1px solid #19525915' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: '#22C55E' }} />
                <p className="text-[9px] font-bold" style={{ color: '#195259' }}>Your page is live!</p>
              </div>
              <div className="rounded-lg p-2.5 flex items-center justify-between" style={{ backgroundColor: '#F5E9CF' }}>
                <p className="text-[8px] font-mono" style={{ color: '#195259' }}>funnely.com/sarah/preset-pack</p>
                <div className="px-2 py-1 rounded text-[7px] font-semibold text-white" style={{ backgroundColor: '#195259' }}>Copy</div>
              </div>
            </div>
            <p className="text-[8px] font-semibold mb-2" style={{ color: '#195259' }}>Share on:</p>
            <div className="flex gap-2">
              {['Instagram Bio', 'TikTok', 'WhatsApp', 'Email'].map(p => (
                <div key={p} className="flex-1 rounded-lg p-2 text-center bg-white text-[7px] font-medium" style={{ border: '1px solid #19525915', color: '#195259' }}>{p}</div>
              ))}
            </div>
            <div className="mt-3 rounded-lg p-3 bg-white" style={{ border: '1px solid #19525915' }}>
              <p className="text-[8px] font-semibold mb-1.5" style={{ color: '#195259' }}>Live Stats</p>
              <div className="grid grid-cols-3 gap-2">
                {[['142', 'Views'], ['89', 'Unique'], ['12', 'Sales']].map(([v, l]) => (
                  <div key={l} className="text-center">
                    <p className="text-sm font-bold" style={{ color: '#FF9A00' }}>{v}</p>
                    <p className="text-[7px]" style={{ color: '#6D6E71' }}>{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        }
      />

      {/* ═══ CTA ═══ */}
      <section className="py-24 sm:py-32 px-4" style={{ backgroundColor: '#195259' }}>
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <img src="/images/marketing/cta-mascot.png" alt="" loading="lazy" className="w-[200px] sm:w-[260px] h-auto mx-auto mb-8 object-contain" style={{ animation: 'float 4s ease-in-out infinite' }} />
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-5">Ready to start?</h2>
            <p className="text-lg mb-10" style={{ color: '#F5E9CF', opacity: 0.8 }}>5 steps. 5 minutes. Your digital store is live.</p>
            <Button size="lg" className="text-base px-12 h-14 font-semibold text-[#195259] rounded-full shadow-lg hover:shadow-xl transition-shadow" style={{ backgroundColor: '#FF9A00' }} onClick={() => navigate('/register')}>
              Create Your Store Now <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Reveal>
        </div>
      </section>

      <style>{`@keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }`}</style>

      {/* Footer */}
      <footer className="py-10 px-4" style={{ backgroundColor: '#0F3338' }}>
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div><img src="/logos/funnely-white.png" alt="Funnely" className="h-5 object-contain" /><p className="text-xs mt-1" style={{ color: '#6D6E71' }}>The easiest way to sell digital products.</p></div>
          <div className="flex items-center gap-6 text-sm" style={{ color: '#6D6E71' }}>
            <a href="/features" className="hover:text-white transition-colors">Features</a>
            <a href="/#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="/login" className="hover:text-white transition-colors">Log In</a>
          </div>
          <p className="text-xs" style={{ color: '#6D6E71' }}>&copy; 2026 Funnely. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
